import { isErrorCode } from '@freightapi/core/errors/codes'
import type { Problem } from '@freightapi/core/errors/problem'
import type { MessageKey } from '@freightapi/i18n'
import type { SignLocale } from './locale'

/**
 * The one way this app talks to the API. Everything goes to the app's own origin: the sign Worker
 * proxies `/api/*` over a service binding to `freightapi-api` (ADR 0009), so there is no CORS
 * preflight on a mobile connection and no API base URL to configure per environment.
 *
 * The token endpoints themselves land in M1-10; M2-01 fixes the transport, the error mapping and
 * the locale header so the screens in M2-03 and M2-09 only have to describe their own shapes.
 */

/** Prefix the Worker strips before handing the request to the API. */
export const API_PREFIX = '/api'

export interface ApiRequest {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  readonly body?: unknown
  readonly locale?: SignLocale
  readonly signal?: AbortSignal
  readonly headers?: Readonly<Record<string, string>>
}

export interface ApiUpload {
  readonly locale?: SignLocale
  readonly signal?: AbortSignal
  /** How much of the body has left the device, between 0 and 1. */
  readonly onProgress?: (fraction: number) => void
}

/**
 * A failed call, already reduced to something a screen can render. `messageKey` is a `sign`
 * catalogue key when the error has a known meaning for a reader, so the caller translates rather
 * than prints the API's English title.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly problem: Problem | undefined
  readonly messageKey: MessageKey<'sign'>

  constructor(init: {
    status: number
    code: string
    messageKey: MessageKey<'sign'>
    problem?: Problem
    detail?: string
  }) {
    super(init.detail ?? init.problem?.detail ?? init.problem?.title ?? init.code)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.problem = init.problem
    this.messageKey = init.messageKey
  }

  /** True while the reader can still do something about it: retrying is worth offering. */
  get isRetryable(): boolean {
    // An abort is this app's own doing — a screen that was left, a photo that was removed — so
    // there is nothing for the reader to retry.
    if (this.code === 'aborted') return false
    return this.status === 0 || this.status === 429 || this.status >= 500
  }
}

/**
 * Error codes with a reader-facing meaning in the sign flow. Anything outside this table is a
 * technical failure and gets the generic message: a driver cannot act on "idempotency_key_reused".
 */
const MESSAGE_KEY_BY_CODE: Partial<Record<string, MessageKey<'sign'>>> = {
  token_expired: 'status.expired.title',
  token_revoked: 'status.revoked.title',
  link_expired: 'status.expired.title',
  token_rotated: 'status.expired.title',
  already_signed: 'status.used.title',
  not_found: 'error.not_found.title',
}

export async function apiFetch<T>(path: string, request: ApiRequest = {}): Promise<T> {
  const { method = 'GET', body, locale, signal, headers } = request

  const headerBag: Record<string, string> = { accept: 'application/json', ...headers }
  if (locale !== undefined) headerBag['accept-language'] = locale
  if (body !== undefined) headerBag['content-type'] = 'application/json'

  let response: Response
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers: headerBag,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: signal ?? null,
      credentials: 'omit',
    })
  } catch (cause) {
    // A dropped mobile connection, a DNS failure or an aborted navigation: no status to report.
    throw new ApiError({
      status: 0,
      code: 'network_error',
      messageKey: 'error.network',
      detail: cause instanceof Error ? cause.message : undefined,
    })
  }

  if (response.status === 204) return undefined as T
  if (!response.ok) throw await problemFrom(response)

  return (await response.json()) as T
}

/**
 * A file upload, which `fetch()` cannot report the progress of. A driver in a loading bay pushes a
 * megabyte and a half up a cell connection per photo, so a bar that moves is the difference between
 * "it is working" and "it is broken"; `XMLHttpRequest` is the only browser API that says so, and it
 * is the reason this one call does not go through {@link apiFetch}. Everything else — the `/api`
 * prefix, the locale header, the problem mapping — is the same.
 *
 * The `content-type` is left to the browser: a multipart body needs the boundary it generated.
 */
export function apiUpload<T>(path: string, form: FormData, request: ApiUpload = {}): Promise<T> {
  const { locale, signal, onProgress } = request

  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(abortedError())
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_PREFIX}${path}`)
    xhr.responseType = 'text'
    xhr.withCredentials = false
    xhr.setRequestHeader('accept', 'application/json')
    if (locale !== undefined) xhr.setRequestHeader('accept-language', locale)

    const abort = () => xhr.abort()
    signal?.addEventListener('abort', abort, { once: true })
    const settle = () => signal?.removeEventListener('abort', abort)

    if (onProgress !== undefined) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total)
      })
    }

    xhr.addEventListener('load', () => {
      settle()
      if (xhr.status === 204) {
        resolve(undefined as T)
        return
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(problemOf(xhr.status, xhr.getResponseHeader('content-type') ?? '', xhr.responseText))
        return
      }
      try {
        resolve(JSON.parse(xhr.responseText) as T)
      } catch {
        reject(
          new ApiError({
            status: xhr.status,
            code: 'invalid_response',
            messageKey: 'error.unexpected',
            detail: 'The upload was accepted but the answer was not JSON.',
          }),
        )
      }
    })

    xhr.addEventListener('error', () => {
      settle()
      reject(networkError('The upload could not reach the server.'))
    })
    xhr.addEventListener('timeout', () => {
      settle()
      reject(networkError('The upload timed out.'))
    })
    xhr.addEventListener('abort', () => {
      settle()
      reject(abortedError())
    })

    xhr.send(form)
  })
}

async function problemFrom(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('content-type') ?? ''
  return problemOf(response.status, contentType, await readBody(response))
}

/** Never throws: an error page is worse than an error message without a body. */
function problemOf(status: number, contentType: string, body: string): ApiError {
  const problem = readProblem(contentType, body, status)
  const code = problem?.code ?? String(status)

  return new ApiError({
    status,
    code,
    messageKey: (isErrorCode(code) ? MESSAGE_KEY_BY_CODE[code] : undefined) ?? 'error.unexpected',
    ...(problem === undefined ? {} : { problem }),
  })
}

function readProblem(contentType: string, body: string, status: number): Problem | undefined {
  if (!contentType.includes('json')) return undefined
  try {
    const parsed = JSON.parse(body) as Partial<Problem>
    if (typeof parsed?.code !== 'string' || typeof parsed?.title !== 'string') return undefined
    return { errors: [], warnings: [], status, type: '', ...parsed } as Problem
  } catch {
    return undefined
  }
}

/** A body that cannot be read is an empty one; the status still carries the meaning. */
async function readBody(response: Response): Promise<string> {
  try {
    return await response.text()
  } catch {
    return ''
  }
}

function networkError(detail: string): ApiError {
  return new ApiError({ status: 0, code: 'network_error', messageKey: 'error.network', detail })
}

/** An abort is the caller's own doing, so it is never retryable and never a screen. */
function abortedError(): ApiError {
  return new ApiError({
    status: 0,
    code: 'aborted',
    messageKey: 'error.unexpected',
    detail: 'The upload was cancelled.',
  })
}
